'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useFrame } from '@react-three/fiber';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { createRenderPlaybackClock } from '../../runtime/renderPlaybackClock.js';
import { useTransitionPlayback } from '../../runtime/useTransitionPlayback.js';
import { PORTAL_PREVIEW_SOURCE_ASPECT, resolvePortalPreviewMatrix, resolvePortalPreviewPose, } from '../_pure/portalPreview.js';
import SpaceRenderer from './SpaceRenderer.js';
const STENCIL_REF = 1;
/**
 * Everything here is drawn after the surrounding scene: first the opening marks the stencil, then the
 * opening is filled with the previewed background at far depth, then the previewed scene is drawn
 * where the stencil is marked. Content orders leave room below for negative authored orders.
 */
const MASK_ORDER = 10_000;
const FILL_ORDER = MASK_ORDER + 1;
const CONTENT_ORDER = MASK_ORDER + 100;
const FAR_FILL_VERTEX = /* glsl */ `
void main() {
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    gl_Position.z = gl_Position.w;
}
`;
const FAR_FILL_FRAGMENT = /* glsl */ `
uniform vec3 color;
void main() {
    gl_FragColor = vec4(color, 1.0);
    #include <colorspace_fragment>
}
`;
const ignoreRaycast = () => { };
/** Clips every material below root to the opening and draws it after the surrounding scene. */
function clipToOpening(root, clipped) {
    root.traverse((object) => {
        const material = object.material;
        if (!material)
            return;
        if (object.renderOrder < MASK_ORDER)
            object.renderOrder += CONTENT_ORDER;
        object.raycast = ignoreRaycast;
        for (const entry of Array.isArray(material) ? material : [material]) {
            // Opaque materials would draw before the opening is marked.
            if (!entry.transparent) {
                entry.transparent = true;
                entry.needsUpdate = true;
            }
            if (clipped.has(entry))
                continue;
            clipped.add(entry);
            entry.stencilWrite = true;
            entry.stencilRef = STENCIL_REF;
            entry.stencilFunc = THREE.EqualStencilFunc;
            entry.stencilFail = THREE.KeepStencilOp;
            entry.stencilZFail = THREE.KeepStencilOp;
            entry.stencilZPass = THREE.KeepStencilOp;
        }
    });
}
/** Stands in for the viewer's camera so the previewed transitions move a virtual one. */
function createPreviewCamera(initial) {
    let pose = initial;
    return {
        referenceFrame: () => 'desktop',
        read: () => pose,
        apply: (next) => { pose = next; },
        stage: (_frame, next) => { pose = next; },
        setMode: () => { },
        setGuided: () => { },
        viewportAspect: () => PORTAL_PREVIEW_SOURCE_ASPECT,
    };
}
/** Plays the previewed outputs as they arrive, keeping the content placed so the moving camera framing fills the opening. */
function PortalPreviewPlayback(props) {
    const { portal, preview } = props;
    const { output, playback } = preview;
    const content = useRef(null);
    const clipped = useMemo(() => new WeakSet(), []);
    const initialBase = useRef(output.base);
    // One virtual camera per session, so guided travel continues from wherever the previous beat left it.
    const camera = useMemo(() => createPreviewCamera(resolvePortalPreviewPose(initialBase.current)), []);
    // Window animation frames stop during immersive XR; the render loop keeps running.
    const [renderClock] = useState(() => createRenderPlaybackClock());
    const frame = useTransitionPlayback(output, camera, playback?.onTransitionComplete, playback?.onPlaybackActivityChange, false, playback?.resetKey ?? 0, playback?.onTransitionPhaseComplete, renderClock.clock);
    // Guided travel moves the stand-in camera, not the frame's authored camera, so the framing follows it.
    const placedPose = useRef(undefined);
    useFrame(() => {
        renderClock.tick();
        const group = content.current;
        if (!group)
            return;
        const pose = camera.read();
        if (pose && pose !== placedPose.current) {
            placedPose.current = pose;
            group.matrix.copy(resolvePortalPreviewMatrix(portal, pose));
            group.matrixWorldNeedsUpdate = true;
        }
        clipToOpening(group, clipped);
    });
    return (_jsx("group", { ref: content, matrixAutoUpdate: false, userData: { xrTravelIgnore: true }, children: _jsx(SpaceRenderer, { derivationFrame: frame, lighting: false }) }));
}
/**
 * Draws a derived scene inside a portal's opening. The host drives it like a space runtime: it plays
 * each output's transitions and reports their completion, so a host session can advance through beats.
 */
export function PortalLiveView(props) {
    const { portal, preview } = props;
    const background = preview.output.base.spaceRenderOptions?.backgroundColor ?? '#000000';
    const mask = useMemo(() => new THREE.MeshBasicMaterial({
        colorWrite: false, depthWrite: false, transparent: true,
        stencilWrite: true, stencilRef: STENCIL_REF, stencilFunc: THREE.AlwaysStencilFunc,
        stencilZPass: THREE.ReplaceStencilOp,
    }), []);
    const fill = useMemo(() => new THREE.ShaderMaterial({
        uniforms: { color: { value: new THREE.Color() } },
        vertexShader: FAR_FILL_VERTEX, fragmentShader: FAR_FILL_FRAGMENT,
        transparent: true, depthWrite: true, depthFunc: THREE.AlwaysDepth,
        stencilWrite: true, stencilRef: STENCIL_REF, stencilFunc: THREE.EqualStencilFunc,
    }), []);
    useEffect(() => () => {
        mask.dispose();
        fill.dispose();
    }, [mask, fill]);
    useLayoutEffect(() => {
        fill.uniforms.color.value.set(background);
    }, [fill, background]);
    return (_jsxs(_Fragment, { children: [_jsxs("group", { position: portal.center, rotation: [0, Math.atan2(portal.facing[0], portal.facing[2]), 0], children: [_jsx("mesh", { material: mask, renderOrder: MASK_ORDER, children: _jsx("planeGeometry", { args: [portal.width, portal.height] }) }), _jsx("mesh", { material: fill, renderOrder: FILL_ORDER, frustumCulled: false, children: _jsx("planeGeometry", { args: [portal.width, portal.height] }) })] }), _jsx(PortalPreviewPlayback, { portal: portal, preview: preview })] }));
}
